/**
 * Utilities for working with QuickSight embed URLs.
 *
 * QuickSight embed URLs have this structure:
 * https://ap-southeast-2.quicksight.aws.amazon.com/embed/{sessionId}/dashboards/{dashboardId}?code=...&identityprovider=quicksight&isauthcode=true
 *
 * We cache only the session base URL (everything up to /embed/{sessionId}) because:
 * 1. The session is valid for 30 minutes regardless of which dashboard is viewed
 * 2. We can construct URLs for any dashboard using the same session
 */

const AUTH_PARAMS = ['code', 'isauthcode'];

/**
 * Checks if the URL has auth params (fresh from API, not yet used for embed).
 */
export const hasAuthParams = (url: string): boolean => {
  try {
    const urlObj = new URL(url);

    return AUTH_PARAMS.some((param) => urlObj.searchParams.has(param));
  } catch {
    return false;
  }
};

/**
 * Checks if the URL is a session base URL (no /dashboards/ path).
 */
export const isSessionBaseUrl = (url: string): boolean => {
  return !url.includes('/dashboards/');
};

/**
 * Strips one-time authentication parameters from QuickSight embed URLs.
 * These params (code, isauthcode) are consumed during the initial SDK handshake
 * and cannot be reused.
 *
 * @example
 * Input:  https://ap-southeast-2.quicksight.aws.amazon.com/embed/xxx/dashboards/yyy?code=abc&identityprovider=quicksight&isauthcode=true
 * Output: https://ap-southeast-2.quicksight.aws.amazon.com/embed/xxx/dashboards/yyy?identityprovider=quicksight
 */
export const stripAuthParams = (url: string): string => {
  try {
    const urlObj = new URL(url);

    AUTH_PARAMS.forEach((param) => urlObj.searchParams.delete(param));

    return urlObj.toString();
  } catch {
    return url;
  }
};

/**
 * Extracts the session base URL from a full QuickSight embed URL.
 * The session base URL is everything up to and including /embed/{sessionId}.
 *
 * @example
 * Input:  https://ap-southeast-2.quicksight.aws.amazon.com/embed/xxx/dashboards/yyy?identityprovider=quicksight
 * Output: https://ap-southeast-2.quicksight.aws.amazon.com/embed/xxx
 */
export const getSessionBaseUrl = (url: string): string => {
  const match = url.match(/(.*\/embed\/[^/?#]+)/);

  return match ? match[1] : url;
};

/**
 * Constructs a full dashboard embed URL from a session base URL and dashboard ID.
 *
 * @example
 * Input:  sessionBaseUrl = 'https://ap-southeast-2.quicksight.aws.amazon.com/embed/xxx'
 *         dashboardId = 'yyy'
 * Output: https://ap-southeast-2.quicksight.aws.amazon.com/embed/xxx/dashboards/yyy?identityprovider=quicksight
 */
export const buildDashboardUrl = (sessionBaseUrl: string, dashboardId: string): string => {
  // Remove trailing slash if present
  const baseUrl = sessionBaseUrl.replace(/\/$/, '');

  return `${baseUrl}/dashboards/${dashboardId}?identityprovider=quicksight`;
};
